import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { Label, TextField } from "@nuorder/components";
import { AlertNotification } from "components/alert/alert-notification";
import { DATATEST } from "constants/integration";
import { COMMA_DELIMITER, Kinds, SEMICOLON_DELIMITER, SeverityMessages } from "constants/product-collection";
import { fetchSuggestionsFromInternalLookup } from "helpers/internal-lookups/size-curves-lookup";
import { ISchemaKey } from "helpers/product-collection";
import { formatMessage } from "lib/globalize-util";
import me from "models/me";
import React, { FC, useCallback, useRef, useState } from "react";
import Select from "react-select";
import { MarketSettingsData } from "../market-settings.types";
import { styles, customStylesForAlert } from "./styles";

interface IOwnProps {
    rawItemDisplayProperty: ISchemaKey[];
    merchantId: string;
    assortmentSettings: MarketSettingsData;
    setAssortmentSettings: (MarketSettingsData) => void;
    readonly: boolean;
}

interface IOption {
    value: string;
    label: string;
}

export const MarketSettingsDepartment: FC<IOwnProps> = ({
    rawItemDisplayProperty,
    merchantId,
    assortmentSettings,
    setAssortmentSettings,
    readonly,
}) => {
    const [lookupOptions, setLookUpOptions] = useState<IOption[]>();
    const [searchValue, setSearchValue] = useState<string>("");
    const [searchSelection, setSearchSelection] = useState<IOption>();
    const [lookUpDisplayName, setLookUpLabel] = useState<string>();
    const [lookUpDerivativeDisplayNames, setLookUpDerivativeLabels] = useState<string[]>([]);
    const reactSelectRef = useRef<Select>();

    const handleSearchSelection = useCallback(
        (selection: IOption) => {
            const deptName = getDelimeterValue(selection?.label, null);
            setSearchSelection(selection);
            setSearchValue(selection?.value ?? "");
            setAssortmentSettings((prev) => ({
                ...prev,
                departmentId: selection?.value,
                departmentName: deptName,
            }));
        },
        [setSearchSelection, setAssortmentSettings]
    );

    const handleChangeDepartmentNumber = useCallback(
        (_, newValue) => {
            setAssortmentSettings((prev) => ({
                ...prev,
                departmentId: newValue,
            }));
        },
        [setAssortmentSettings]
    );

    const handleSearchInput = (value, { action }) => {
        if (action === "input-blur") {
            setSearchValue(searchSelection?.value ?? "");
        } else if (action === "menu-close" || action === "set-value") {
            return;
        } else {
            setSearchValue(value);
        }
    };

    const handleSetTextFieldLabels = React.useCallback(
        (lookUpField, lookupSchemaNames) => {
            const lookupDerivativeSchemaNames = lookupSchemaNames.filter(
                (schemaName) => schemaName !== lookUpField.field_name
            );

            if (lookupDerivativeSchemaNames.length > 0) {
                // Label for TextFields
                setLookUpDerivativeLabels(
                    rawItemDisplayProperty
                        .filter((item) => lookupDerivativeSchemaNames.includes(item.field_name))
                        .map((item) => item.display_name)
                );
            }
        },
        [rawItemDisplayProperty]
    );

    const handleSetInitialValues = React.useCallback(
        (suggestionOptions) => {
            if (assortmentSettings.departmentId) {
                const existingSearchSelection = suggestionOptions.find(
                    (option) => option.value === assortmentSettings.departmentId
                );

                if (existingSearchSelection) {
                    setSearchSelection(existingSearchSelection);
                    setSearchValue(existingSearchSelection.value);
                }
            }
        },
        [assortmentSettings.departmentId]
    );

    const handleFetchSuggestions = React.useCallback(async () => {
        const lookUpField = rawItemDisplayProperty.find((item) => item.kind === Kinds.DEPARTMENT_LOOKUP);

        if (lookUpField) {
            // Label for Lookup Dropdown
            setLookUpLabel(lookUpField.display_name);
            const { suggestions, lookupSchemaNames } = await fetchSuggestionsFromInternalLookup({
                fieldSchema: lookUpField,
                merchantId,
                rawItemDisplayProperty,
            });
            handleSetTextFieldLabels(lookUpField, lookupSchemaNames);
            const suggestionOptions = suggestions.map((item) => ({
                value: item.key,
                label: item.dropdown_format,
            }));
            setLookUpOptions(suggestionOptions);
            handleSetInitialValues(suggestionOptions);
        }
    }, [rawItemDisplayProperty, merchantId, handleSetTextFieldLabels, handleSetInitialValues]);

    const handleMenuOpen = () => {
        setTimeout(() => {
            const selectedEl = document.getElementsByClassName("departmentDropdown__option--is-selected")[0];
            if (selectedEl) {
                selectedEl.scrollIntoView({ behavior: "smooth" });
                reactSelectRef.current.select.setState({
                    focusedOption: searchSelection,
                });
            }
        }, 0);
    };

    React.useEffect(() => {
        handleFetchSuggestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const selectedDerivativeName = React.useCallback(
        (index) => {
            const label = searchSelection?.label;
            return getDelimeterValue(label, index);
        },
        [searchSelection]
    );

    const getDelimeterValue = (label, index) => {
        let name;
        if (label) {
            let separator = SEMICOLON_DELIMITER;
            if (label.includes(COMMA_DELIMITER)) {
                separator = COMMA_DELIMITER;
            }
            name = label.split(separator)[index ? index + 1 : 1];
        }
        return name;
    };

    const isKindOfUserThatShouldShowTheAlert: boolean = me.isBuyer() || me.isNuOrderUser() || false;
    const shouldOpenAlertMessage = !readonly && !assortmentSettings.departmentId && isKindOfUserThatShouldShowTheAlert;

    return (
        <>
            {lookUpDisplayName ? (
                <div style={styles.row}>
                    <div style={styles.item}>
                        <Label htmlFor="assortment-settings-department-search">{lookUpDisplayName}</Label>
                        <Select
                            ref={reactSelectRef}
                            inputId="assortment-settings-department-search"
                            onMenuOpen={handleMenuOpen}
                            classNamePrefix={"departmentDropdown"}
                            styles={styles}
                            isDisabled={readonly}
                            options={lookupOptions}
                            onChange={handleSearchSelection}
                            value={searchValue === "" ? "" : searchSelection}
                            inputValue={searchValue}
                            onInputChange={handleSearchInput}
                            placeholder={"Search by Name or Number"}
                            isClearable={true}
                            data-test={DATATEST.ASSORTMENTS.ASSORMENT_SETTINGS.LOOK_UP_FIELD}
                            theme={(theme) => ({
                                ...theme,
                                colors: {
                                    ...theme.colors,
                                    primary25: "rgb(246, 246, 246)",
                                    primary: "rgb(47, 199, 190)",
                                },
                            })}
                        />
                    </div>
                    {lookUpDerivativeDisplayNames.map((displayName, index) => (
                        <TextField
                            key={index}
                            data-test={DATATEST.ASSORTMENTS.ASSORMENT_SETTINGS.LOOK_UP_DERIVATIVE_FIELD}
                            value={selectedDerivativeName(index)}
                            label={displayName}
                            placeholder={displayName}
                            readOnly={true}
                            disabled={readonly}
                            styles={styles.textField}
                        />
                    ))}
                </div>
            ) : (
                <TextField
                    data-test={DATATEST.ASSORTMENTS.ASSORMENT_SETTINGS.DEPARTMENT_ID}
                    value={assortmentSettings.departmentId}
                    onChange={handleChangeDepartmentNumber}
                    label={formatMessage("Department #")}
                    placeholder={formatMessage("Department #")}
                    disabled={readonly}
                />
            )}

            {!!assortmentSettings && (
                <AlertNotification
                    customStyles={customStylesForAlert}
                    open={shouldOpenAlertMessage}
                    message={formatMessage("Please fill in your buying department.")}
                    severity={SeverityMessages.Warning}
                    customIcon={<ErrorOutlineIcon />}
                    noMargin
                />
            )}
        </>
    );
};
